import { z } from "zod";

import type { OpenPencilFigmaSourceBundle } from "./contracts.ts";

// Structural copies of the design-profile / design-system-pack-handoff lane
// types (those lanes are not part of this slice). TypeScript's structural
// typing makes the helpers below drop-in compatible when those lanes land;
// the shapes below must stay byte-compatible with that contract.
interface OpenPencilDesignProfileEvidenceRef {
  ref: string;
  kind:
    | "source-artifact"
    | "source-node"
    | "source-metadata"
    | "deterministic-derivation"
    | "model-run"
    | "render"
    | "human-review"
    | "other";
  contentHash?: string;
}

interface OpenPencilDesignProfileSourceAuthority {
  sourceSetId: string;
  authorityClass:
    | "user-owned"
    | "licensed"
    | "reference-only"
    | "mixed"
    | "unverified";
  sourceKinds: string[];
  sourceRefs: OpenPencilDesignProfileEvidenceRef[];
  revisionBindings: Array<{
    sourceRef: string;
    revisionId?: string;
    contentHash: string;
    capturedAt: string;
  }>;
  rightsStatus: "verified" | "pending-review" | "unknown";
}

interface DesignSystemPackEvidenceRef {
  runId: string;
  artifactHash: string;
  mediaType: string;
  uri: string;
}

export const OPENPENCIL_SOURCE_RIGHTS_DECLARATION_SCHEMA =
  "cogo.openpencil-source-rights-declaration.v1" as const;

export const OpenPencilSourceRightsDeclarationSchema = z
  .object({
    schemaVersion: z.literal(OPENPENCIL_SOURCE_RIGHTS_DECLARATION_SCHEMA),
    declarationId: z.string().min(1),
    source: z
      .object({
        sourceId: z.string().min(1),
        sourceRevision: z.number().int().nonnegative(),
        fileName: z.string().min(1),
        fileSha256: z.string().regex(/^[a-f0-9]{64}$/),
        sourceArtifactRef: z.string().min(1),
      })
      .strict(),
    scope: z
      .object({
        tenantId: z.string().min(1),
        projectId: z.string().min(1),
        principalId: z.string().min(1),
      })
      .strict(),
    declaration: z
      .object({
        rightsBasis: z.enum(["user-owned", "licensed", "unverified"]),
        requestedUse: z.enum([
          "project-app-and-pack-review",
          "evaluation-only",
        ]),
        policyVersion: z.literal(
          "cogo.local-fig-rights-declaration-policy.v1",
        ),
      })
      .strict(),
    licenseRef: z.string().min(1).nullable(),
    attribution: z.string().min(1).nullable(),
    declarant: z
      .object({
        actor: z.literal("human"),
        exactRevisionConfirmed: z.literal(true),
      })
      .strict(),
    review: z
      .object({
        status: z.enum(["self-attested", "evaluation-only"]),
        legalVerification: z.literal(false),
        externalReviewRequired: z.boolean(),
        packPromotionAllowed: z.literal(false),
      })
      .strict(),
    authority: z
      .object({
        previewGenerationAllowed: z.literal(true),
        productionGenerationAllowed: z.literal(false),
        retrievalCorpusIngestionAllowed: z.literal(false),
        teacherArtifactInputAllowed: z.literal(false),
        packProposalAllowed: z.literal(false),
        ontologyAdmissionAllowed: z.literal(false),
        canonicalOntologyAuthority: z.literal(false),
        promotionAllowed: z.literal(false),
        modelMayPromote: z.literal(false),
        directTtlWriteAllowed: z.literal(false),
      })
      .strict(),
    declaredAt: z.string().datetime(),
    contentHash: z.string().regex(/^sha256:[a-f0-9]{64}$/),
    evidenceRef: z
      .object({
        runId: z.string().min(1),
        artifactHash: z.string().regex(/^sha256:[a-f0-9]{64}$/),
        mediaType: z.literal(
          "application/vnd.cogo.source-rights-declaration+json",
        ),
        uri: z.string().min(1),
      })
      .strict(),
  })
  .strict()
  .superRefine((value, context) => {
    if (
      (value.declaration.rightsBasis === "licensed") !==
      (value.licenseRef !== null)
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["licenseRef"],
        message: "A licensed declaration requires one license reference.",
      });
    }
    const evaluationOnly =
      value.declaration.requestedUse === "evaluation-only";
    if (
      evaluationOnly !==
        (value.declaration.rightsBasis === "unverified") ||
      evaluationOnly !== (value.review.status === "evaluation-only") ||
      evaluationOnly === value.review.externalReviewRequired
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["declaration"],
        message:
          "Evaluation-only use and unverified rights must remain an explicit quarantine.",
      });
    }
  });

export type OpenPencilSourceRightsDeclaration = z.infer<
  typeof OpenPencilSourceRightsDeclarationSchema
>;

export interface BuildOpenPencilSourceRightsDeclarationInput {
  bundle: OpenPencilFigmaSourceBundle;
  sourceRevision: number;
  scope: OpenPencilSourceRightsDeclaration["scope"];
  rightsBasis: OpenPencilSourceRightsDeclaration["declaration"]["rightsBasis"];
  requestedUse: OpenPencilSourceRightsDeclaration["declaration"]["requestedUse"];
  licenseRef?: string;
  attribution?: string;
  /**
   * REQUIRED. The moment the declaration is being made, decided by the caller.
   *
   * This used to be optional with a `new Date().toISOString()` fallback. The
   * stamp lands inside `base`, so it is covered by `contentHash` and therefore
   * by `declarationId` and `evidenceRef` — a builder that reads the clock mints
   * a different seal on every call and nobody can re-derive the one that was
   * committed. The honest value differs by caller: F0 is a script reproducing a
   * past operator designation and cannot know it, so it takes `--declared-at`
   * and fails closed; Studio is present at the human's confirmation, so it
   * reads its own clock at the point it freezes the rest of its inputs. Neither
   * answer is available to *this* function, which is exactly why it stopped
   * guessing.
   */
  declaredAt: string;
}

const DeclaredAtSchema = z.string().datetime();

/**
 * Captures a user's exact-revision declaration as content-addressed evidence.
 * This is deliberately not a legal verification, signed rights ledger, or
 * Design System Pack promotion decision.
 */
export async function buildOpenPencilSourceRightsDeclaration(
  input: BuildOpenPencilSourceRightsDeclarationInput,
): Promise<OpenPencilSourceRightsDeclaration> {
  // Checked FIRST — before the bundle is read at all — so the refusal is
  // reproducible for a caller that has no source in hand, and so a malformed
  // stamp can never reach `sha256CanonicalJson` and get sealed into an
  // identifier before the schema rejects it. The schema's own leaf is reused
  // rather than re-spelled: two spellings of "valid instant" would drift.
  const declaredAtCheck = DeclaredAtSchema.safeParse(input.declaredAt);
  if (!declaredAtCheck.success) {
    throw new Error("OPENPENCIL_SOURCE_RIGHTS_DECLARED_AT_REQUIRED");
  }
  const declaredAt = declaredAtCheck.data;
  const fileSha256 = input.bundle.fileSha256;
  if (!fileSha256) {
    throw new Error("OPENPENCIL_SOURCE_RIGHTS_FILE_HASH_REQUIRED");
  }
  if (!Number.isInteger(input.sourceRevision) || input.sourceRevision < 0) {
    throw new Error("OPENPENCIL_SOURCE_RIGHTS_REVISION_INVALID");
  }
  const licenseRef = input.licenseRef?.trim() || null;
  if (input.rightsBasis === "licensed" && !licenseRef) {
    throw new Error("OPENPENCIL_SOURCE_RIGHTS_LICENSE_REF_REQUIRED");
  }
  if (input.rightsBasis !== "licensed" && licenseRef) {
    throw new Error("OPENPENCIL_SOURCE_RIGHTS_LICENSE_REF_NOT_APPLICABLE");
  }
  if (
    (input.requestedUse === "evaluation-only") !==
    (input.rightsBasis === "unverified")
  ) {
    throw new Error("OPENPENCIL_SOURCE_RIGHTS_USE_BASIS_MISMATCH");
  }
  const sourceArtifactRef =
    input.bundle.sourceArtifactRef ??
    `cogo-source://openpencil-file/${fileSha256}`;
  const base = {
    schemaVersion: OPENPENCIL_SOURCE_RIGHTS_DECLARATION_SCHEMA,
    source: {
      sourceId: input.bundle.sourceId,
      sourceRevision: input.sourceRevision,
      fileName: input.bundle.fileName,
      fileSha256,
      sourceArtifactRef,
    },
    scope: { ...input.scope },
    declaration: {
      rightsBasis: input.rightsBasis,
      requestedUse: input.requestedUse,
      policyVersion: "cogo.local-fig-rights-declaration-policy.v1" as const,
    },
    licenseRef,
    attribution: input.attribution?.trim() || null,
    declarant: {
      actor: "human" as const,
      exactRevisionConfirmed: true as const,
    },
    review: {
      status:
        input.requestedUse === "evaluation-only"
          ? ("evaluation-only" as const)
          : ("self-attested" as const),
      legalVerification: false as const,
      externalReviewRequired: input.requestedUse !== "evaluation-only",
      packPromotionAllowed: false as const,
    },
    authority: {
      previewGenerationAllowed: true as const,
      productionGenerationAllowed: false as const,
      retrievalCorpusIngestionAllowed: false as const,
      teacherArtifactInputAllowed: false as const,
      packProposalAllowed: false as const,
      ontologyAdmissionAllowed: false as const,
      canonicalOntologyAuthority: false as const,
      promotionAllowed: false as const,
      modelMayPromote: false as const,
      directTtlWriteAllowed: false as const,
    },
    declaredAt,
  };
  const contentHash = await sha256CanonicalJson(base);
  const digest = contentHash.slice("sha256:".length);
  const declaration = {
    ...base,
    declarationId: `source-rights-declaration/${safeIdentifier(
      input.bundle.sourceId,
    )}/${digest.slice(0, 24)}`,
    contentHash,
    evidenceRef: {
      runId: `source-rights-declaration:${input.bundle.sourceId}:${input.sourceRevision}`,
      artifactHash: contentHash,
      mediaType:
        "application/vnd.cogo.source-rights-declaration+json" as const,
      uri: `cogo-source://rights-declaration/${digest}`,
    },
  };
  return OpenPencilSourceRightsDeclarationSchema.parse(declaration);
}

export async function assertOpenPencilSourceRightsDeclarationIntegrity(
  value: unknown,
): Promise<OpenPencilSourceRightsDeclaration> {
  const declaration = OpenPencilSourceRightsDeclarationSchema.parse(value);
  const {
    declarationId: _declarationId,
    contentHash,
    evidenceRef,
    ...base
  } = declaration;
  const expectedHash = await sha256CanonicalJson(base);
  const digest = contentHash.slice("sha256:".length);
  if (
    contentHash !== expectedHash ||
    declaration.declarationId !==
      `source-rights-declaration/${safeIdentifier(
        declaration.source.sourceId,
      )}/${digest.slice(0, 24)}` ||
    evidenceRef.artifactHash !== contentHash ||
    evidenceRef.runId !==
      `source-rights-declaration:${declaration.source.sourceId}:${declaration.source.sourceRevision}` ||
    evidenceRef.uri !== `cogo-source://rights-declaration/${digest}`
  ) {
    throw new Error("OPENPENCIL_SOURCE_RIGHTS_DECLARATION_INTEGRITY_INVALID");
  }
  return declaration;
}

/**
 * Re-scope a declaration to a different project id. The declaration's
 * substance is bound to the SOURCE (sourceId + fileSha256 + sourceRevision)
 * and grants nothing; `scope.projectId` is administrative, so a
 * host-controlled workspace transfer (e.g. binding a validated source
 * workspace to a freshly created project) rewrites only that field. Every
 * attested field — declarant, declaredAt, review, authority, licenseRef,
 * attribution — is preserved byte-for-byte; the canonical-JSON content hash
 * (which covers the scope) and the hash-derived declarationId/evidenceRef are
 * recomputed so integrity verification keeps passing.
 */
export async function rescopeOpenPencilSourceRightsDeclarationProject(
  value: OpenPencilSourceRightsDeclaration,
  projectId: string,
): Promise<OpenPencilSourceRightsDeclaration> {
  const declaration =
    await assertOpenPencilSourceRightsDeclarationIntegrity(value);
  if (declaration.scope.projectId === projectId) return declaration;
  const {
    declarationId: _declarationId,
    contentHash: _contentHash,
    evidenceRef: _evidenceRef,
    ...base
  } = declaration;
  const rescopedBase = {
    ...base,
    scope: { ...base.scope, projectId },
  };
  const contentHash = await sha256CanonicalJson(rescopedBase);
  const digest = contentHash.slice("sha256:".length);
  return OpenPencilSourceRightsDeclarationSchema.parse({
    ...rescopedBase,
    declarationId: `source-rights-declaration/${safeIdentifier(
      rescopedBase.source.sourceId,
    )}/${digest.slice(0, 24)}`,
    contentHash,
    evidenceRef: {
      runId: `source-rights-declaration:${rescopedBase.source.sourceId}:${rescopedBase.source.sourceRevision}`,
      artifactHash: contentHash,
      mediaType:
        "application/vnd.cogo.source-rights-declaration+json" as const,
      uri: `cogo-source://rights-declaration/${digest}`,
    },
  });
}

export function sourceRightsDeclarationEvidenceRef(
  declaration: OpenPencilSourceRightsDeclaration,
): DesignSystemPackEvidenceRef {
  return { ...declaration.evidenceRef };
}

export function sourceAuthorityFromRightsDeclaration(
  declaration: OpenPencilSourceRightsDeclaration,
): OpenPencilDesignProfileSourceAuthority {
  const sourceRef: OpenPencilDesignProfileEvidenceRef = {
    ref: declaration.source.sourceArtifactRef,
    kind: "source-artifact",
    contentHash: `sha256:${declaration.source.fileSha256}`,
  };
  return {
    sourceSetId: declaration.source.sourceId,
    authorityClass:
      declaration.declaration.requestedUse === "evaluation-only"
        ? "reference-only"
        : declaration.declaration.rightsBasis,
    sourceKinds: ["fig-local", "openpencil"],
    sourceRefs: [sourceRef],
    revisionBindings: [
      {
        sourceRef: sourceRef.ref,
        revisionId: String(declaration.source.sourceRevision),
        contentHash: sourceRef.contentHash!,
        capturedAt: declaration.declaredAt,
      },
    ],
    rightsStatus:
      declaration.declaration.requestedUse === "evaluation-only"
        ? "unknown"
        : "pending-review",
  };
}

function safeIdentifier(value: string): string {
  const safe = value
    .normalize("NFKD")
    .replace(/[^A-Za-z0-9._-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase();
  return safe || "unknown-source";
}

function canonicalize(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(canonicalize);
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value)
        .filter(([, entry]) => entry !== undefined)
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([key, entry]) => [key, canonicalize(entry)]),
    );
  }
  return value;
}

async function sha256CanonicalJson(value: unknown): Promise<string> {
  const bytes = new TextEncoder().encode(JSON.stringify(canonicalize(value)));
  const digest = await globalThis.crypto.subtle.digest("SHA-256", bytes);
  return `sha256:${[...new Uint8Array(digest)]
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("")}`;
}
